import React, { useMemo, useRef, useState } from "react";

const clean = (v) => String(v ?? "").trim();
const norm = (v) => clean(v).toLowerCase();

// Value can be string | string[] | null
const asArray = (v) => {
  if (Array.isArray(v)) return v;
  if (v == null) return [];
  const s = clean(v);
  return s ? [s] : [];
};

export default function MultiSelectField({
  label,
  value,
  options = [], // [{label, name}]
  onChange,
  disabled = false,
  placeholder = "Type to search…",
  hint,
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  // Normalize selected values (lowercase) to avoid mixed-case issues
  const selected = useMemo(() => {
    const arr = asArray(value).map(norm).filter(Boolean);
    return [...new Set(arr)];
  }, [value]);

  // Build fast lookup for display labels
  const optionIndex = useMemo(() => {
    const m = new Map(); // normValue -> {label,value}
    for (const opt of options || []) {
      const val = norm(opt?.name ?? opt?.value);
      if (!val) continue;
      m.set(val, {
        label: clean(opt?.label) || clean(opt?.name ?? opt?.value),
        value: val, // store normalized slug
      });
    }
    return m;
  }, [options]);

  const selectedChips = useMemo(() => {
    return selected.map((v) => optionIndex.get(v) || { label: v, value: v });
  }, [selected, optionIndex]);

  const filtered = useMemo(() => {
    const qq = norm(q);
    const out = [];
    for (const opt of options || []) {
      const v = norm(opt?.name ?? opt?.value);
      if (!v) continue;
      if (selected.includes(v)) continue;

      const lbl = clean(opt?.label);
      if (!qq || norm(lbl).includes(qq) || v.includes(qq)) {
        out.push({ label: lbl || v, value: v });
      }
    }
    // small UX: sort by label
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
  }, [q, options, selected]);

  const emit = (next) => {
    const uniq = [...new Set((next || []).map(norm).filter(Boolean))];
    onChange?.(uniq);
  };

  const add = (v) => {
    const vv = norm(v);
    if (!vv) return;
    if (selected.includes(vv)) return;
    emit([...selected, vv]);
    setQ("");
    setOpen(false);
    // keep focus for rapid tag entry
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const remove = (v) => {
    const vv = norm(v);
    emit(selected.filter((x) => x !== vv));
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const onKeyDown = (e) => {
    if (disabled) return;

    if (e.key === "Backspace" && !q) {
      // remove last chip
      if (selected.length) remove(selected[selected.length - 1]);
    }

    if (e.key === "Enter") {
      e.preventDefault();
      // add first match
      if (filtered.length) add(filtered[0].value);
    }

    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label ? (
        <label style={{ fontWeight: 600 }}>{label}</label>
      ) : null}

      <div
        style={{
          border: "1px solid #ddd",
          borderRadius: 8,
          padding: 8,
          background: disabled ? "#f7f7f7" : "#fff",
        }}
        onClick={() => {
          if (!disabled) inputRef.current?.focus();
        }}
      >
        {/* chips row */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {selectedChips.map((chip) => (
            <span
              key={chip.value}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 8px",
                border: "1px solid #ccc",
                borderRadius: 999,
                fontSize: 13,
                background: "#fafafa",
              }}
            >
              <span>{chip.label}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(chip.value)}
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    fontSize: 14,
                    lineHeight: 1,
                    padding: 0,
                  }}
                  aria-label={`Remove ${chip.label}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}

          {/* input */}
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => {
              // small delay so clicks on dropdown register
              setTimeout(() => setOpen(false), 120);
            }}
            onKeyDown={onKeyDown}
            disabled={disabled}
            placeholder={selected.length ? "" : placeholder}
            style={{
              flex: 1,
              minWidth: 160,
              border: "none",
              outline: "none",
              fontSize: 14,
              background: "transparent",
            }}
          />
        </div>

        {/* dropdown */}
        {open && !disabled && filtered.length > 0 && (
          <div
            style={{
              marginTop: 8,
              border: "1px solid #ddd",
              borderRadius: 8,
              overflow: "hidden",
              maxHeight: 220,
              overflowY: "auto",
              background: "#fff",
            }}
          >
            {filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onMouseDown={(e) => e.preventDefault()} // keep focus
                onClick={() => add(opt.value)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 12px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                }}
              >
                {opt.label}
                <span style={{ opacity: 0.6, marginLeft: 8, fontSize: 12 }}>
                  {opt.value}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {hint ? <div style={{ fontSize: 12, opacity: 0.75 }}>{hint}</div> : null}
    </div>
  );
}
